import { Icon, merge, StackItem } from '@fluentui/react';
import { CommandBar, ConstrainMode, DetailsList, ScrollablePane, ScrollbarVisibility, SearchBox, SelectionMode, Stack, Sticky, StickyPositionType } from 'office-ui-fabric-react';
import { DetailsHeader } from 'office-ui-fabric-react/lib/components/DetailsList/DetailsHeader';
import { Selection } from 'office-ui-fabric-react/lib/Utilities';
import React, { Component, Fragment } from 'react';
import Scriba from 'react-scriba';

// Ces clefs permettent d'identifier les colonnes où sont stockées des valeurs monétaires ; ça nous permet d'appliquer des styles particuliers
const currencyKeys = [
    "engagementCA_CumuleAnneeNMoins1",
    "engagementCA_CumuleMoisM",
    "prixHT",
    "reintegrationCA",
    "reintegrationCA_CumuleMoisMMoins1PourAnneeN",
];

export class EUIDetailsList extends Component {

    // En cours de test : DetailsListTache, DetailsListTicket, DetailsListCapsuleClient
    // KP - 28/06/2023 : conserver la sélection lors du tri, du lazy loading et lors de l'application des filtres
    // KP - 05/07/2023 : centralisation du render des colonnes communes + fix lazy loading
    // KP - 01/09/2023 : ajout d'un event sur le compteur d'items (pour debug plus facilement)
    // KP - 20/09/2023 : implémentation du drag-and-drop (pour FicheActualite)

    static defaultProps = {
        activeItems: [], // 
        canSelectItem: undefined,
        columns: [],
        farMenuItems: [],
        getKey: item => item.key, // Détermine quel identifiant utiliser pour distinguer chaque item de la liste (ex : item => item.idOperation)
        keysToUncheck: [],
        hideCommandBar: false,
        hideHeader: false,
        isLazyLoadingEnabled: true,
        isSortedDescending: false,
        items: [],
        menuItems: [],
        onColumnHeaderClick: undefined, // (sortedColumn, isSortedDescending) => null
        onCounterClick: undefined, // () => null
        onDragAndDropped: undefined, // (reorderedItems) => null
        onFilterButtonClick: undefined, // () => null
        onRenderRow: undefined, // () => null
        onScroll: undefined, // () => null
        onSearch: undefined, // newValue => null
        onSelectionChanged: undefined, // items => null
        selectionMode: SelectionMode.none,
        scrollablePaneStyles: undefined,
        sortedColumn: undefined,
        wrapInScrollablePane: true
    };

    constructor(props) {
        super(props);
        this._draggedIndex = -1;
        this._enableScrollEvent = true;
        this._selection = new Selection({
            canSelectItem: props.canSelectItem,
            onSelectionChanged: this._onSelectionChanged
        });
        this._selectedKeys = [];
    };

    _getColumns = () => {

        const { isSortedDescending, onDragAndDropped, sortedColumn } = this.props;

        const currencyFormat = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });
        const percentFormat = new Intl.NumberFormat("fr-FR", { style: "percent", minimumFractionDigits: 2 });
        const longPercentFormat = new Intl.NumberFormat("fr-FR", { style: "percent", minimumFractionDigits: 10 });

        let updatedColumns = [...this.props.columns];
        if (typeof onDragAndDropped === "function")
            updatedColumns.unshift({
                isIconOnly: true,
                key: "drag",
                onRender: () => <Icon iconName="ScrollUpDown" title="Saisir" styles={{ root: { cursor: "grab" } }} />
            });

        updatedColumns = updatedColumns.map(column => {

            const { fieldName, isIconOnly, key } = column;

            // SortedColumn + isSortedDescending
            let updatedColumn = {
                ...column,
                isSorted: sortedColumn === key,
                isSortedDescending: sortedColumn === key && isSortedDescending,
            };

            // Icônes
            if (isIconOnly) {
                updatedColumn.isResizable = false;
                updatedColumn.minWidth = 20;
                updatedColumn.maxWidth = 20;
            }
            else updatedColumn.isResizable = true;

            // Dates
            if (key === "creerLe" || key === "modifierLe" || key.startsWith("date")) {
                updatedColumn.minWidth = 100;
                updatedColumn.maxWidth = 100;
                updatedColumn.onRender = item => {
                    const date = Scriba.date().stringDateFormatCourt(item[key]);
                    const dateHeure = Scriba.date().stringDateHeureFormatCourt(item[key]);
                    return <span title={dateHeure}>{date}</span>;
                }
            }

            // Valeurs monétaires
            if (key.startsWith("prix") || key.startsWith("montant") || currencyKeys.includes(key)) {
                updatedColumn.minWidth = 100;
                updatedColumn.maxWidth = 120;
                updatedColumn.onRender = item => {
                    const value = item[key];
                    const formated = !!value || value === 0 ? currencyFormat.format(value) : null; // Avoid -0
                    return <span style={{ display: "block", textAlign: "right" }} title={formated}>{formated}</span>;
                }
            }

            // Pourcentages
            if (key.startsWith("pourcentage")) {
                updatedColumn.minWidth = 60;
                updatedColumn.maxWidth = 60;
                updatedColumn.onRender = item => {
                    const value = item[key];
                    const title = !!value || value === 0 ? longPercentFormat.format(value) : null;
                    const formated = !!value || value === 0 ? percentFormat.format(value) : null;
                    return <span style={{ display: "block", textAlign: "right" }} title={title}>{formated}</span>;
                }
            }

            // IDs
            else if (key.startsWith("id")) {
                updatedColumn.isResizable = false;
                updatedColumn.minWidth = 30;
                updatedColumn.maxWidth = 30;
                updatedColumn.name = updatedColumn.name || "#";
            }

            // Render par défaut
            if (!fieldName && !updatedColumn.onRender)
                updatedColumn.onRender = item => {
                    const value = item[key];
                    return <span title={value}>{value}</span>;
                }

            return updatedColumn;

        })

        return updatedColumns;

    };

    _getDragDropEvents = () => {
        return {
            canDrop: (dropContext, dragContext) => true,
            canDrag: (item) => true,
            onDragEnter: (item, event) => null,
            onDragLeave: (item, event) => null,
            onDrop: (item, event) => {
                if (this._draggedItem)
                    this._insertBeforeItem(item);
            },
            onDragStart: (item, itemIndex, selectedItems, event) => {
                this._draggedItem = item;
                this._draggedIndex = itemIndex;
            },
            onDragEnd: (item, event) => {
                this._draggedItem = undefined;
                this._draggedIndex = -1;
            },
        };
    };

    _insertBeforeItem = (item) => {

        const { items, onDragAndDropped } = this.props;

        const draggedItems = this._selection.isIndexSelected(this._draggedIndex)
            ? this._selection.getSelection()
            : [this._draggedItem];

        const insertIndex = items.indexOf(item); // Index de l'item sur lequel on a relaché le bouton de la souris
        let reorderedItems = items.filter(image => draggedItems.indexOf(image) === -1); // A quoi ça sert ?
        reorderedItems.splice(insertIndex, 0, ...draggedItems); // Insertion des draggedItems (pas de suppression)

        onDragAndDropped(reorderedItems);

    };

    _getItems = () => {

        const { menuItems, onSearch } = this.props;
        let items = [];

        if (typeof onSearch === "function")
            items.push({
                key: "search",
                onRender: () => <SearchBox
                    onClear={event => onSearch(null)}
                    onSearch={newValue => onSearch(newValue)}
                    placeholder="Recherche rapide"
                    styles={{
                        root: {
                            background: "white",
                            height: "100%",
                            marginRight: "15px",
                            width: "250px",
                        }
                    }}
                />
            });

        if (!!menuItems && Array.isArray(menuItems))
            items.push(...menuItems);

        return items;

    };

    _getFarItems = () => {

        const { farMenuItems, isLazyLoadingEnabled, items, onCounterClick, onFilterButtonClick } = this.props;

        const selection = this._selection.getSelection();
        let name = items.length;
        if (selection.length > 0) name = `${selection.length} sél. / ${name}`;

        let farItems = Array.isArray(farMenuItems) ? [...farMenuItems] : [];

        farItems.push({
            checked: !isLazyLoadingEnabled,
            key: "itemsLength",
            name,
            onClick: event => event.ctrlKey && typeof onCounterClick === "function" && onCounterClick() // Maintenir la touche CTRL enfoncée
        });

        if (typeof onFilterButtonClick === "function")
            farItems.push({
                key: "filtre",
                name: "Filtres",
                iconProps: { iconName: "Filter" },
                iconOnly: true,
                onClick: onFilterButtonClick
            });

        return farItems;

    };

    _onColumnHeaderClick = (ev, column) => {
        const { onColumnHeaderClick } = this.props;
        if (typeof onColumnHeaderClick === "function") {
            const key = column.key;
            const isSortedDescending = !column.isSortedDescending;
            onColumnHeaderClick(key, isSortedDescending);
        }
    };

    _onRenderDetailsHeader = (props, defaultRender) => {
        const { hideHeader } = this.props;
        if (hideHeader) return;
        else {
            let detailsHeader = null;

            // Alignement à droite des entêtes des valeurs monétaires
            let detailsHeaderStyles = {};
            currencyKeys.forEach(key => {
                merge(
                    detailsHeaderStyles,
                    { root: { selectors: { [`span[class^='ms-DetailsHeader-cellTitle'][id*='${key}']`]: { flexDirection: "row-reverse" } } } },
                );
            });

            if (!props.columns.some(column => !!column.headerName)) detailsHeader = defaultRender({ ...props, styles: detailsHeaderStyles });
            else {

                // Calcul de la hauteur du header
                const baseHeight = 32;
                const lineHeight = 16;
                const headerHeight = lineHeight + (lineHeight * Math.max(
                    ...props.columns // Parmi toutes les colonnes du tableau
                        .filter(column => !!column.headerName) // Parmi celles qui possèdent une entête
                        .map(column => column.headerName.split("\n").filter(row => !!row).length) // Déterminer le nombre de lignes
                ));

                const border = "1px solid #dee2e6";
                merge(
                    detailsHeaderStyles,
                    {
                        root: {
                            height: `${baseHeight + headerHeight}px`,
                            padding: 0,
                            selectors: {
                                ".ms-DetailsHeader-cell": { height: baseHeight + headerHeight, padding: 0 },
                                ".ms-DetailsHeader-cellIsCheck": { display: "inline-block" },
                                "span[class^='cellTooltip'], span[class^='checkTooltip']": {
                                    inset: "unset",
                                    width: "100%"
                                },
                            }
                        },
                        cellSizer: {
                            height: baseHeight,
                            top: headerHeight,
                        }
                    }
                );

                const onRenderColumnHeaderTooltip = (tooltipHostProps, dr) => {

                    let index = tooltipHostProps.children._owner.pendingProps.columnIndex - 1;
                    if (props.selectionMode !== SelectionMode.none) index--; // Cas particulier : index décalé lorsqu'on autorise la sélection sur la DetailsList

                    const column = props.columns[index] || {};
                    const previousColumn = index >= 1 ? props.columns[index - 1] : null;
                    const nextColumn = index < props.columns.length - 1 ? props.columns[index + 1] : null;

                    const previousHeaderName = previousColumn && previousColumn.headerName;
                    const headerName = column.headerName;
                    const nextHeaderName = nextColumn && nextColumn.headerName;
                    const displayedHeaderName = headerName !== previousHeaderName ? headerName : null;

                    let styles = {
                        root: {
                            backgroundColor: !!headerName ? "#f4f4f4" : "white",
                            display: "flex",
                            flexDirection: "column",
                            height: headerHeight,
                            justifyContent: "center",
                            padding: "0px 8px 0px 12px",
                        }
                    };

                    const rowStyles = {
                        root: {
                            fontSize: 12,
                            height: lineHeight,
                            lineHeight
                        }
                    };

                    // Bordure gauche
                    if (!!headerName && headerName !== previousHeaderName) { // Si entête différente de l'entête précédente
                        styles.root.borderLeft = border;
                        styles.root.position = "relative";
                        styles.root.zIndex = 1;
                    }

                    // Bordure droite
                    if (!!headerName && !nextHeaderName) // Si pas d'entête suivante (ou indirectement si dernière colonne)
                        styles.root.borderRight = border;

                    return <Fragment>
                        {!!displayedHeaderName
                            ? <Stack styles={styles}>
                                {displayedHeaderName
                                    .split("\n")
                                    .filter(row => !!row)
                                    .map((row, rowIndex) => <StackItem key={rowIndex} styles={rowStyles}>{row}</StackItem>)
                                }
                            </Stack>
                            : <Stack styles={styles} />
                        }
                        {dr(tooltipHostProps)}
                    </Fragment>;

                }

                detailsHeader = <DetailsHeader
                    {...props}
                    onRenderColumnHeaderTooltip={onRenderColumnHeaderTooltip}
                    styles={detailsHeaderStyles}
                />;

            }

            return <Sticky stickyPosition={StickyPositionType.Header} isScrollSynced={true}>
                {detailsHeader}
            </Sticky >;
        }
    };

    _onRenderRow = (props, defaultRender) => {
        const { onRenderRow } = this.props;
        const styles = {
            checkCell: { alignItems: "center", display: "flex" },
            fields: { alignItems: "center" }
        };
        if (typeof onRenderRow === "function") return onRenderRow({ ...props, styles }, defaultRender);
        else return defaultRender({ ...props, styles });
    };

    _onSelectionChanged = () => {
        const { onSelectionChanged } = this.props;
        if (typeof onSelectionChanged === "function")
            onSelectionChanged(this._selection.getSelection());
    };

    _onScroll = (event) => {
        const { onScroll } = this.props;
        if (typeof onScroll === "function") {
            const { scrollHeight, scrollTop, clientHeight } = event.target;
            // scrollHeight = hauteur totale du ScrollablePane (visible + non visible)
            // clientHeight = hauteur affichée à l'écran du ScrollablePane
            // scrollTop = distance verticale déjà parcourue
            if (scrollHeight - scrollTop - clientHeight <= 1) {
                // Timeout utilisé pour empêcher que onScroll se déclenche plusieurs fois de suite
                if (this._enableScrollEvent) {
                    this._enableScrollEvent = false;
                    const timer = setTimeout(() => this._enableScrollEvent = true, 1000);
                    onScroll();
                    return () => clearTimeout(timer);
                }
            }
        }
    };

    componentDidMount = () => {
        const { getKey } = this.props;
        if (typeof getKey === "function")
            this._selection.getKey = getKey;
    };

    componentDidUpdate = (prevProps) => {

        const { activeItems, getKey, items, keysToUncheck } = this.props;

        // Force la dé-sélection d'items (à la demande du parent)
        if (JSON.stringify(prevProps.keysToUncheck) !== JSON.stringify(keysToUncheck))
            if (Array.isArray(keysToUncheck))
                keysToUncheck.forEach(key => this._selection.setKeySelected(key, false));

        if (Array.isArray(activeItems)) {
            // Mise à jour de la sélection à la demande du parent
            // A voir, doit potentiellement remplacer keysToUncheck car activeItems commune à toutes les listes migrées FC
            const activeItemsHasChanged = JSON.stringify(prevProps.activeItems) !== JSON.stringify(activeItems);
            const itemsHasChanged = JSON.stringify(prevProps.items) !== JSON.stringify(items);
            if (activeItemsHasChanged || itemsHasChanged) {
                const selection = this._selection.getSelection();
                const count = this._selection.getSelectedCount();
                if (!Array.isArray(activeItems)) this._selection.setAllSelected(false);

                // Pour chacun des éléments de la sélection, vérifier lequel a été désélectionné par le parent
                else if (activeItems.length < count) {
                    selection.forEach(item => {
                        const key = item.key;
                        const stillActive = activeItems.some(activeItem => getKey(activeItem) === key);
                        if (!stillActive) this._selection.setKeySelected(key, false);
                    });
                }

                // Pour chacun des éléments nouvellement communiqués par le parent, chercher quelle clef mettre à jour
                else if (activeItems.length > count) {
                    activeItems.forEach(activeItem => {
                        const key = getKey(activeItem);
                        const shouldActive = !selection.some(item => item.key === key);
                        if (shouldActive) this._selection.setKeySelected(key, true);
                    });
                }
            }
        }
        else {
            // Re-sélection des éléments déjà sélectionnés dans le cas ou activeItems n'est pas fourni dans les props
            this._selectedKeys.forEach(key => this._selection.setKeySelected(key, true));
        }

    };

    componentWillUpdate = (nextProps, nextState) => {
        const selection = this._selection.getSelection();
        this._selectedKeys = selection.map(item => this._selection.getKey(item));
    };

    render = () => {

        const { hideCommandBar, onDragAndDropped, scrollablePaneStyles, wrapInScrollablePane } = this.props;

        let detailsList = <DetailsList
            {...this.props}
            ariaLabelForSelectAllCheckbox="Sélectionner / désélectionner tout"
            ariaLabelForSelectionColumn="Modifier la sélection"
            checkButtonAriaLabel="Ajouter à la sélection"
            dragDropEvents={typeof onDragAndDropped === "function" ? this._getDragDropEvents() : null}
            columns={this._getColumns()}
            constrainMode={ConstrainMode.unconstrained}
            onColumnHeaderClick={this._onColumnHeaderClick}
            onRenderDetailsHeader={this._onRenderDetailsHeader}
            onRenderRow={this._onRenderRow}
            selection={this._selection}
            selectionPreservedOnEmptyClick
        />;

        if (wrapInScrollablePane) {
            let styles = { root: { position: "relative", height: "100%" } };
            if (!!scrollablePaneStyles)
                merge(styles, scrollablePaneStyles);

            detailsList = <ScrollablePane
                onScroll={this._onScroll}
                scrollbarVisibility={ScrollbarVisibility.auto}
                styles={styles}
            >
                {detailsList}
            </ScrollablePane>;
        }

        return <Stack verticalFill={wrapInScrollablePane}>
            {!hideCommandBar &&
                <CommandBar
                    ariaLabel="Utiliser les flèches droite et gauche pour naviguer"
                    farItems={this._getFarItems()}
                    items={this._getItems()}
                    overflowButtonProps={{ ariaLabel: "Plus de fonctionnalités" }}
                />
            }
            {detailsList}
        </Stack>;

    };

};